import { useState, useEffect } from 'react';
import { Cookie, X, ChevronDown, ChevronUp, Shield, BarChart2, Settings } from 'lucide-react';

const COOKIE_KEY = 'ift_cookie_consent';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  preferences: boolean;
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    preferences: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_KEY);
    if (!saved) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (customPrefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({
      prefs: customPrefs,
      date: new Date().toISOString(),
    }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[200] flex items-end justify-center p-4 pointer-events-none">
      <div
        className="w-full max-w-lg bg-card border-2 border-accent/30 rounded-2xl shadow-2xl shadow-black/40 pointer-events-auto animate-in slide-in-from-bottom-4 duration-300"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
              <Cookie className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h3 className="font-black uppercase tracking-wider text-sm text-foreground">
                Preferencias de Cookies
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: 'sans-serif' }}>
                Usamos cookies para mejorar tu experiencia
              </p>
            </div>
          </div>
          <button
            onClick={() => saveConsent({ essential: true, analytics: false, preferences: false })}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pb-3 space-y-3">
          <p className="text-xs text-muted-foreground leading-relaxed" style={{ fontFamily: 'sans-serif' }}>
            Usamos cookies esenciales para el funcionamiento de la app. Con tu permiso, también usamos cookies de análisis.
            Consulta nuestra{' '}
            <a href="/privacidad" className="text-accent underline underline-offset-2">
              Política de Privacidad
            </a>
            {' '}y{' '}
            <a href="/terminos" className="text-accent underline underline-offset-2">
              Términos y Condiciones
            </a>.
          </p>

          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent hover:text-accent/80 transition-colors"
          >
            <Settings className="w-3.5 h-3.5" />
            Configurar preferencias
            {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {showDetails && (
            <div className="space-y-2 bg-secondary/30 rounded-xl p-3 border border-border">
              {/* Essential — always on */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-500" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide">Esenciales</p>
                    <p className="text-[10px] text-muted-foreground" style={{ fontFamily: 'sans-serif' }}>
                      Sesión, autenticación, seguridad
                    </p>
                  </div>
                </div>
                <div className="w-8 h-4 bg-green-500/60 rounded-full flex items-center justify-end px-0.5 cursor-not-allowed">
                  <div className="w-3 h-3 bg-white rounded-full" />
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart2 className="w-4 h-4 text-accent" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide">Análisis</p>
                    <p className="text-[10px] text-muted-foreground" style={{ fontFamily: 'sans-serif' }}>
                      Mejora de la experiencia de uso
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}
                  className={`w-8 h-4 rounded-full flex items-center px-0.5 transition-all duration-200 ${
                    prefs.analytics ? 'bg-accent justify-end' : 'bg-secondary justify-start'
                  }`}
                >
                  <div className="w-3 h-3 bg-white rounded-full shadow" />
                </button>
              </div>

              {/* Preferences */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4 text-blue-400" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide">Preferencias</p>
                    <p className="text-[10px] text-muted-foreground" style={{ fontFamily: 'sans-serif' }}>
                      Idioma, tema, configuración
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, preferences: !p.preferences }))}
                  className={`w-8 h-4 rounded-full flex items-center px-0.5 transition-all duration-200 ${
                    prefs.preferences ? 'bg-accent justify-end' : 'bg-secondary justify-start'
                  }`}
                >
                  <div className="w-3 h-3 bg-white rounded-full shadow" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="px-5 pb-5 grid grid-cols-2 gap-2">
          <button
            onClick={() => saveConsent({ essential: true, analytics: false, preferences: false })}
            className="py-2.5 rounded-xl border-2 border-secondary text-xs font-black uppercase tracking-wider text-muted-foreground hover:border-accent/30 hover:text-foreground transition-all"
          >
            Solo Esenciales
          </button>
          <button
            onClick={() => saveConsent(
              showDetails
                ? prefs
                : { essential: true, analytics: true, preferences: true }
            )}
            className="py-2.5 rounded-xl bg-accent text-accent-foreground text-xs font-black uppercase tracking-wider hover:bg-accent/90 transition-all shadow-lg shadow-accent/20"
          >
            {showDetails ? 'Guardar' : 'Aceptar Todas'}
          </button>
        </div>
      </div>
    </div>
  );
}
